import {
  CSSProperties,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface IProps {
  ref: RefObject<HTMLVideoElement>;
  disabled?: boolean;
  isAutoPlay?: boolean;
  posterType: IPosterType;
  posterHideDelay?: number;
  onPlay?: () => void;
}
// background-size 적용할 수 없을 때는 image를 사용한다
export type IPosterType = 'background' | 'image';

export default function useVideoAutoPlay({
  ref,
  disabled,
  isAutoPlay = true,
  posterType,
  posterHideDelay = 300,
  onPlay,
}: IProps) {
  const isLoadedRef = useRef(false);
  const canPlayRef = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showImgPoster, setShowImgPoster] = useState(posterType === 'image');

  const load = useCallback(() => {
    const video = ref.current;
    if (!video || isLoadedRef.current) return;

    const children = video.children as HTMLCollectionOf<HTMLSourceElement>;
    for (const source of children) {
      source.src = source.dataset.src || '';
    }
    video.load();
    isLoadedRef.current = true;
  }, [ref]);

  const play = useCallback(() => {
    if (canPlayRef.current && ref.current) {
      ref.current.play();
      onPlay?.();
    }
  }, [ref, onPlay]);

  const initCurrentTime = useCallback(() => {
    if (ref.current) {
      ref.current.pause();
      ref.current.currentTime = 0;
    }
  }, [ref]);

  const stop = useCallback(() => {
    canPlayRef.current && initCurrentTime();
  }, [initCurrentTime]);

  useEffect(() => {
    const video = ref.current;
    if (!video || disabled) return;

    const onCanPlayThrough = () => {
      video.play().then(() => {
        if (!canPlayRef.current) {
          initCurrentTime();
          setTimeout(() => {
            if (posterType === 'background') {
              video.style.background = 'none';
            } else {
              setShowImgPoster(false);
            }
            canPlayRef.current = true;
            setIsLoaded(true);
            isAutoPlay && video.currentTime === 0 && play();
          }, posterHideDelay);
          video.removeEventListener('canplaythrough', onCanPlayThrough);
        }
      });
    };
    video.addEventListener('canplaythrough', onCanPlayThrough);
    return () => {
      video.removeEventListener('canplaythrough', onCanPlayThrough);
    };
  }, [
    ref,
    initCurrentTime,
    posterType,
    posterHideDelay,
    play,
    isAutoPlay,
    disabled,
  ]);

  return {load, play, stop, showImgPoster, isLoaded};
}

export function getPosterStyle({
  posterType,
  poster,
  style,
  isBgSizeCover,
}: {
  posterType: IPosterType;
  poster?: string;
  style?: CSSProperties;
  isBgSizeCover?: boolean;
}) {
  return posterType === 'background' && poster
    ? {
        background: `url(${poster}) center top / ${
          isBgSizeCover ? 'cover' : 'contain'
        } no-repeat`,
        ...style,
      }
    : style;
}
